import React from "react";
import {BulbOutlined} from "@ant-design/icons";
import {Text, TextAs, TextSizes} from "@clockout-dev/design-elements";
import SorIdentitySection from "./SorIdentitySection";
import SorEmploymentIncomeSection from "./SorEmploymentIncomeSection";
import SorPaystubsSection from "./SorPaystubsSection";
import SorDdAccountSection from "./SorDdAccountSection";
import SorAttemptHistorySection from "./SorAttemptHistorySection";
import SorAccountSettingsSection from "./SorAccountSettingsSection";
import SorCoreOwnerSection from "./SorCoreOwnerSection";
import styles from "../styles/sor-composer.module.scss";

/** Keys that are defaults, not authored data, when deciding "filled". */
const DEFAULT_KEYS = new Set(["country", "currency", "compensation_unit", "source", "type"]);

function hasAnyValue(value, key) {
    if (value === null || value === undefined) return false;
    if (typeof value === "string") return value.trim() !== "";
    if (typeof value === "number") return Number.isFinite(value);
    if (typeof value === "boolean") return value;
    if (Array.isArray(value)) return value.some(entry => hasAnyValue(entry));
    if (typeof value === "object") return Object.entries(value).some(([entryKey, entryValue]) => !DEFAULT_KEYS.has(entryKey) && hasAnyValue(entryValue, entryKey));
    return false;
}

/**
 * Orchestrates the SOR sections. Purely presentational — receives the shared
 * `sor` object and forwards section-level changes up via `onSectionChange`.
 * The core-owner section only appears for the full manual flow, which is the
 * only mode that derives identity from the core record.
 *
 * @param {Object} props
 * @param {string} props.mode active simulator mode (tab key)
 * @param {Object} props.sor authored SOR
 * @param {Record<string, string>} [props.errors] validation errors by dot-path
 * @param {string[]} [props.notes] server-preset notes to surface
 * @param {(sectionKey: string, value: *) => void} props.onSectionChange
 */
function SorComposer({mode, sor, errors, notes, onSectionChange}) {
    const value = sor || {};

    return (
        <div className={styles.composer}>
            {notes?.length ? (
                <div className={styles.notes}>
                    {notes.map((note, index) => (
                        <Text key={index} as={TextAs.P} size={TextSizes.EXTRA_SMALL} className={styles.noteLine}>
                            <BulbOutlined className={styles.noteIcon} />
                            {note}
                        </Text>
                    ))}
                </div>
            ) : null}
            {errors?.sor ? (
                <Text as={TextAs.P} size={TextSizes.EXTRA_SMALL} className={styles.rootError}>
                    {errors.sor}
                </Text>
            ) : null}

            <SorEmploymentIncomeSection
                employment={value.employment}
                income={value.income}
                errors={errors}
                filled={hasAnyValue(value.employment) || hasAnyValue(value.income)}
                onEmploymentChange={employment => onSectionChange("employment", employment)}
                onIncomeChange={income => onSectionChange("income", income)}
            />
            <SorPaystubsSection paystubs={value.recent_paystubs} errors={errors} filled={hasAnyValue(value.recent_paystubs)} onChange={paystubs => onSectionChange("recent_paystubs", paystubs)} />
            {mode === "manual_batch" ? (
                <SorCoreOwnerSection coreOwner={value.core_owner} filled={hasAnyValue(value.core_owner)} onChange={coreOwner => onSectionChange("core_owner", coreOwner)} />
            ) : null}
            <SorIdentitySection identity={value.identity} errors={errors} filled={hasAnyValue(value.identity)} onChange={identity => onSectionChange("identity", identity)} />
            <SorDdAccountSection
                account={value.direct_deposit_account}
                fiOverrides={value.fi_overrides}
                errors={errors}
                filled={hasAnyValue(value.direct_deposit_account) || hasAnyValue(value.fi_overrides)}
                onAccountChange={account => onSectionChange("direct_deposit_account", account)}
                onFiOverridesChange={fiOverrides => onSectionChange("fi_overrides", fiOverrides)}
            />
            <SorAttemptHistorySection
                attempts={value.recent_intake_attempts}
                errors={errors}
                filled={hasAnyValue(value.recent_intake_attempts)}
                onChange={attempts => onSectionChange("recent_intake_attempts", attempts)}
            />
            <SorAccountSettingsSection
                settings={value.settings}
                syncStatus={value.sync_status}
                noSync={value.no_sync}
                filled={hasAnyValue(value.settings) || !!value.sync_status || !!value.no_sync}
                onSettingsChange={settings => onSectionChange("settings", settings)}
                onSyncStatusChange={syncStatus => onSectionChange("sync_status", syncStatus)}
                onNoSyncChange={noSync => onSectionChange("no_sync", noSync)}
            />
        </div>
    );
}

export default SorComposer;
